import logger from '../../../../utils/logging/logger';
import { formatErrorMessage } from './helpers';

// Pure(ish) pieces of createPaymentIntent's body, extracted VERBATIM so the
// useCallback in useCreatePaymentIntent.js stays under the line-count cap.
// Each takes exactly the values/setters it touches, in the same order the
// original inline code touched them.

// The response.freeEnrollment === true / redirectTo === 'confirmation' branch.
// Returns true if it navigated away (caller should return early), false if
// the caller should continue with the normal (paid) payment flow.
export const handleFreeEnrollmentSuccess = (response, totalAmount, {
  navigate,
  setProcessing,
  paymentIntentCreatedRef,
  paymentIntentCreationLockRef,
}) => {
  const isFreeEnrollmentResponse = response.freeEnrollment === true || response.redirectTo === 'confirmation';
  const isZeroAmount = totalAmount === 0 || response.amount === 0;
  const shouldHandleAsFree = isFreeEnrollmentResponse && isZeroAmount;

  console.log('[FREE_ENROLLMENT] Checking if response is free enrollment:', {
    isFreeEnrollmentResponse,
    isZeroAmount,
    shouldHandleAsFree,
    freeEnrollment: response.freeEnrollment,
    redirectTo: response.redirectTo,
    totalAmount,
    responseAmount: response.amount
  });

  if (!shouldHandleAsFree) {
    console.log('[FREE_ENROLLMENT] ⚠️ Response is NOT a free enrollment - continuing with normal payment flow', {
      isFreeEnrollmentResponse,
      isZeroAmount,
      hasClientSecret: !!response.clientSecret
    });
    return false;
  }

  console.log('[FREE_ENROLLMENT] ✅ Free enrollment detected - navigating to confirmation', {
    freeEnrollment: response.freeEnrollment,
    redirectTo: response.redirectTo,
    totalAmount,
    responseAmount: response.amount,
    courses: response.courses,
    user: response.user
  });

  logger.info('Free enrollment completed - redirecting to confirmation', {
    freeEnrollment: response.freeEnrollment,
    redirectTo: response.redirectTo,
    totalAmount,
    responseAmount: response.amount
  });
  // Navigate to confirmation page with enrollment data
  // Structure data to match what PurchaseConfirmation expects
  const purchaseData = {
    success: true,
    message: response.message || 'Enrollment successful (free course)',
    courses: response.courses || [],
    user: response.user || {},
    enrolledCourseIds: response.enrolledCourseIds || [],
    payment: response.payment || {
      amount: 0,
      currency: response.currency || 'usd',
      receiptUrl: null, // No receipt for free enrollments
      paymentIntentId: response.paymentIntentId || null,
      status: 'completed'
    },
    promoCode: response.promoCode || null, // Include promo code if available
    redirectTo: 'confirmation'
  };

  console.log('[FREE_ENROLLMENT] 🚀 Navigating to confirmation page with data:', {
    purchaseData,
    courseCount: purchaseData.courses.length,
    hasUser: !!purchaseData.user,
    hasPayment: !!purchaseData.payment
  });

  navigate('/confirmation', {
    state: { purchaseData }
  });
  setProcessing(false);
  paymentIntentCreatedRef.current = false; // Reset ref since we're not creating a payment intent
  paymentIntentCreationLockRef.current = false; // Release lock on success
  console.log('[FREE_ENROLLMENT] ✅ Navigation initiated, returning early');
  return true;
};

// The catch(error) block: classify, log, and surface a user-facing error —
// or, for a $0 total whose error response actually carries free-enrollment
// data, still navigate to confirmation.
export const handleCreatePaymentIntentError = (error, totalAmount, {
  navigate,
  setError,
  setProcessing,
  lastPaymentIntentAttemptRef,
  paymentIntentCreatedRef,
  paymentIntentCreationLockRef,
}) => {
  console.error('[FREE_ENROLLMENT] ❌ Error creating payment intent:', {
    errorMessage: error.message,
    errorStatus: error.status || error.response?.status,
    errorName: error.name,
    totalAmount,
    errorData: error.data || error.response?.data,
    errorKeys: Object.keys(error || {}),
    hasErrorData: !!(error.data || error.response?.data)
  });

  logger.error('Error creating payment intent', {
    error,
    status: error.status || error.response?.status,
    errorData: error.data || error.response?.data,
    errorDetails: (error.data || error.response?.data)?.details,
    errorType: (error.data || error.response?.data)?.type,
    errorCode: (error.data || error.response?.data)?.code,
    totalAmount
  });

  // An error is an error: a failed free enrollment must never be shown as a completed one, so the
  // error body is only read for its message.
  // The API service attaches error data as error.data (not error.response.data)
  const errorData = error.data || error.response?.data || error.response || null;
  const backendMessage = errorData?.message || errorData?.error || errorData?.details;


  // FIX #11: Use formatErrorMessage for consistent error message formatting
  // FIX #20: Distinguish between network errors, timeout errors, and server errors
  // FIX #28: Add retry mechanism for transient errors
  const isTimeoutError = error.isTimeout || error.message?.includes('timeout') || error.name === 'AbortError';
  const isNetworkError = !error.status && !error.response && !error.data && (error.message?.includes('fetch') || error.message?.includes('network'));
  const errorStatus = error.status || error.response?.status || errorData?.status;
  const isRetryable = isTimeoutError || isNetworkError || (errorStatus >= 500 && errorStatus < 600);

  // FIX: Handle 409 Conflict errors specifically (already enrolled, course full, etc.)
  let errorMessage;
  if (errorStatus === 409) {
    // 409 Conflict - user is already enrolled or course is full
    errorMessage = backendMessage || 'You are already enrolled in this course or the course is full.';
    logger.warn('409 Conflict - enrollment issue', {
      message: errorMessage,
      errorData
    });
  } else if (errorStatus === 500) {
    // 500 Internal Server Error - show backend error details if available
    errorMessage = backendMessage || 'Server error occurred while creating payment intent. Please try again.';
  } else {
    errorMessage = isTimeoutError
      ? 'Request timed out. Please check your internet connection and try again.'
      : isNetworkError
      ? 'Network error. Please check your connection and try again.'
      : (backendMessage || error.message || 'Failed to create payment intent');
  }

  setError(formatErrorMessage(errorMessage + (isRetryable ? ' You can try again by applying or removing a promo code.' : '')));
  setProcessing(false);
  // Reset the last attempt time on error to allow retry after delay
  lastPaymentIntentAttemptRef.current = null;
  paymentIntentCreationLockRef.current = false; // Release lock on error
  return false;
};
